import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Alert, Box, Button, Chip, Dialog, IconButton, LinearProgress, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { tokens } from '../../theme';
import { contentService, courseService } from '../../services/api';
import ContentSearchMessage, { searchContent } from '../common/ContentSearch';

const T = tokens;
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_FILES = 5;
// The real stages, in the prototype's wording, and real file sizes (K21.2 pts 20, 21). Colours come from the theme
// tokens (T.color.brand), not the prototype's hex (the row's design note).
// American English on every screen (K2.3 pt 17): 'Summarizing'.
const PIPELINE_STAGES = ['Uploading', 'Reading', 'Summarizing', 'Indexing'];
const STAGE_INDEX = { uploading: 0, uploaded: 1, reading: 1, summarizing: 2, indexing: 3, suggesting: 3 };
// Buttons in the mock's three kinds (K21.2 pt 20.1): solid black for the main action, outline for the others, ghost for
// Skip; btnSmall for the buttons inside a row.
const btnBase = { textTransform: 'none', fontFamily: T.font.family, fontSize: T.font.size.md, fontWeight: T.font.weight.medium, borderRadius: T.radius.md, px: 1.75, py: 1, boxShadow: 'none', border: '1px solid', transition: 'transform .2s ease', '&:hover': { transform: 'translateY(-1px)', boxShadow: 'none' } };
const btnSolid = { ...btnBase, backgroundColor: T.color.textPrimary, color: '#fff', borderColor: T.color.textPrimary, '&:hover': { ...btnBase['&:hover'], backgroundColor: T.color.textPrimary }, '&.Mui-disabled': { backgroundColor: T.color.bgTertiary, borderColor: T.color.bgTertiary, color: T.color.textTertiary } };
// The outline is the app's one outlined style (K2.3 pt 15): no fill, a dark border and dark text, the tertiary
// background on hover. Removed 2 Nov 2026 (K2.3 pt 15): its light grey border, darkening on hover.
const btnOut = { ...btnBase, backgroundColor: 'transparent', color: T.color.textPrimary, borderColor: T.color.textPrimary, '&:hover': { ...btnBase['&:hover'], backgroundColor: T.color.bgTertiary, borderColor: T.color.textPrimary } };
const btnGhost = { ...btnBase, backgroundColor: 'transparent', borderColor: 'transparent', color: T.color.textSecondary, '&:hover': { ...btnBase['&:hover'], backgroundColor: 'transparent' } };
const btnSmall = { px: 1.25, py: 0.6, fontSize: T.font.size.sm };
const formatFileSize = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};
const createBatchId = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
// A course description as the course builder's editor stores it: the text escaped, one paragraph per line, blank lines
// left out (K21.2 pt 46, PR #8 review). Some screens show descriptions as HTML (the learning path builder's course cards),
// and the pop-up's text comes from the AI, written from what it read in the files, so markup in a file has to arrive as
// text. This goes once S-26 pt 5 (not built yet) makes every description plain text.
const asEditorHtml = (text) => String(text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  .map((line) => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('');
const metadataFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: T.color.bgPrimary,
    borderRadius: T.radius.sm,
    fontFamily: T.font.family,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.color.borderSecondary },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.color.borderPrimary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.color.brand, borderWidth: 1 },
  },
  '& .MuiInputBase-input': { fontFamily: T.font.family, fontSize: T.font.size.md },
};

const MetadataLabel = ({ children, htmlFor }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: 'block',
      fontFamily: T.font.family,
      fontSize: T.font.size.xs,
      color: T.color.textSecondary,
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

const audienceOptions = [
  { value: '1', label: 'Internal only' },
  { value: '2', label: 'External only' },
  { value: '3', label: 'Everyone' },
];

const stageProgress = {
  uploaded: { label: 'Reading', progress: 28, index: 1 },
  reading: { label: 'Reading', progress: 28, index: 1 },
  summarizing: { label: 'Summarizing', progress: 52, index: 2 }, // K2.3 pt 17
  indexing: { label: 'Indexing', progress: 76, index: 3 },
  suggesting: { label: 'Suggesting skills', progress: 90, index: 3 },
  retrying: { label: 'Retrying', progress: 12, index: 0 },
  duplicate: { label: 'Possible duplicate', progress: 32, index: 1 },
  alreadyExists: { label: 'Already uploaded', progress: 0, index: 0 },
  skipped: { label: 'Skipped', progress: 0, index: 0 },
  completed: { label: 'Ready', progress: 100, index: 4 },
};

// Poll one file's processing status until it finishes. Used after an upload and after Try again.
const pollIngestStatus = (contentId, onEvent, onPollCancelReady) => new Promise((resolve, reject) => {
  let cancelled = false;
  let activePollController = null;
  let pendingDelay = null;
  const cancelPolling = () => {
    cancelled = true;
    activePollController?.abort();
    if (pendingDelay) {
      clearTimeout(pendingDelay.timer);
      pendingDelay.resolve();
      pendingDelay = null;
    }
    resolve();
  };
  onPollCancelReady(cancelPolling);
  (async () => {
    let missing = 0;
    for (let attempt = 0; attempt < 3600; attempt += 1) {
      if (cancelled) return;
      activePollController = new AbortController();
      let statusResponse = null;
      try {
        statusResponse = await contentService.getBatchIngestStatus(contentId, { signal: activePollController.signal });
        missing = 0;
      } catch (error) {
        if (cancelled || error.name === 'CanceledError' || error.name === 'AbortError') return;
        // A brief 404 or network blip (server restart) is not a failure: keep polling for a while.
        missing += 1;
        if (missing > 30) throw new Error('Lost track of this file. Check the content list for its latest status.');
      } finally {
        activePollController = null;
      }
      if (cancelled) return;
      const status = statusResponse?.data?.data;
      if (status) {
        onEvent(status);
        if (['completed', 'limitReached', 'skipped', 'removed'].includes(status.stage)) {
          resolve();
          return;
        }
        if (status.stage === 'failed') {
          const failure = new Error(status.message || 'Content processing failed.');
          failure.reported = true;
          reject(failure);
          return;
        }
      }
      await new Promise((wait) => {
        pendingDelay = {
          timer: setTimeout(() => { pendingDelay = null; wait(); }, 1000),
          resolve: wait,
        };
      });
    }
    throw new Error('Content processing is taking longer than expected. Check the content list for its latest status.');
  })().catch(reject);
});

const uploadAndProcessFile = (file, audienceType, batchId, batchFileCount, onEvent, onUploadProgress, onPollCancelReady, extraFields = {}, onAbortReady = () => {}) => new Promise((resolve, reject) => {
  const request = new XMLHttpRequest();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('audienceType', audienceType);
  formData.append('batchId', batchId);
  formData.append('batchFileCount', batchFileCount);
  // Added for a failed search (K21.2 pt 35): prioritized=1 and the search term.
  Object.entries(extraFields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') formData.append(key, value);
  });
  // Removing a row while it uploads stops the upload (K21.2 pt 25). Once the whole file has reached the server,
  // stopping it here would not stop the server, which is already creating the record: the screen would never
  // learn its id, and the file would be processed and published. So from then on the reply is awaited and the
  // removed row's record is removed by id (the removed-row branch in processEntries).
  let bodySent = false;
  request.upload.onload = () => { bodySent = true; };
  onAbortReady(() => { if (!bodySent) request.abort(); });
  request.onabort = () => resolve();

  request.open('POST', '/api/contents/batch-upload');
  request.withCredentials = true;
  request.upload.onprogress = (event) => {
    if (event.lengthComputable) onUploadProgress(Math.round((event.loaded / event.total) * 100));
  };
  request.onload = async () => {
    try {
      let response = {};
      try {
        response = JSON.parse(request.responseText);
      } catch {
        throw new Error(request.status === 413 ? 'This file is over the 50 MB limit.' : 'Upload failed. Try again.');
      }
      if (response.duplicate) {
        const error = new Error(response.message || `${file.name} is already in your library.`);
        error.alreadyExists = true;
        throw error;
      }
      if (request.status < 200 || request.status >= 300 || !response.success || !response.contentId) {
        throw new Error(response.message || 'Upload failed.');
      }
      if (!/\.zip$/i.test(file.name)) {
        onEvent({ stage: 'uploaded', contentId: response.contentId, title: response.title });
      } else {
        // A SCORM file shows no Reading stage; this only gives a row removed during its upload the record's id.
        onEvent({ stage: 'received', contentId: response.contentId });
      }
      await pollIngestStatus(response.contentId, onEvent, onPollCancelReady);
      resolve();
    } catch (error) {
      reject(error);
    }
  };
  request.onerror = () => reject(new Error('Network error while uploading this file.'));
  request.send(formData);
});

const AddContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Opened from a failed search (Search Intelligence or Insights): files added here are marked as a
  // priority in search, and a single file takes the search term as its title (K21.2 pt 35).
  const searchTerm = String(location.state?.prefill || '').trim();
  const fromFailedSearch = !!searchTerm && location.state?.is_prioritized === true;
  const [audienceType, setAudienceType] = useState(null);
  const [limits, setLimits] = useState(null);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [allowanceChecking, setAllowanceChecking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [creatingCourse, setCreatingCourse] = useState(false);
  // The course pop-up (K21.2 pt 46): null while closed; otherwise what it shows and its own state.
  const [courseDraft, setCourseDraft] = useState(null);
  // The pop-up keeps its name and description for the batch while the page stays open (K21.2 pt 46.1 (a)): Cancel and
  // '+ Create a course from this' again shows them as left, with no new AI call, even once more of the batch's files are
  // ready (its modules and skills are read again each time).
  const courseTextRef = useRef(null);
  // Removed 23 Oct 2026 (K21.2 pt 20.1, PR #8 review): fileNotice ("... is ready.", "... details saved."), set but never
  // shown: its alert was commented out before the rebuild, and the rebuild dropped that comment.
  const [tryQuestion, setTryQuestion] = useState('');
  const [tryItResponse, setTryItResponse] = useState(null);
  const [tryItLoading, setTryItLoading] = useState(false);
  const tryAskRef = useRef(0);
  // The area under Try it's box keeps its height while it checks (K21.2 pt 36.1): its height is taken as a check starts.
  const tryAreaRef = useRef(null);
  const [tryAreaHeight, setTryAreaHeight] = useState(0);
  // Ready rows the author has folded away (K21.2 pt 20.1).
  const [collapsedRows, setCollapsedRows] = useState(() => new Set());
  const toggleRow = (id) => setCollapsedRows((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const activeJobsRef = useRef(0);
  const pollCancelsRef = useRef(new Map());
  const uploadAbortsRef = useRef(new Map());
  const removedRowsRef = useRef(new Set());
  const removedRecordsRef = useRef(new Set());
  const batchIdRef = useRef(createBatchId());

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      // Removed 9 Oct 2026 (K21.1 pt 17): .doc (old Word files cannot be read; save as .docx) and the .mpeg and
      // .mpga audio names (not played or transcribed here).
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/wav': ['.wav'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'application/scorm+zip': ['.zip'],
    },
    maxSize: MAX_FILE_SIZE,
    maxFiles: Math.max(MAX_FILES - files.length, 0),
    multiple: true,
    noClick: true,
    noKeyboard: true,
    disabled: !audienceType || files.length >= MAX_FILES || allowanceChecking,
    onDropAccepted: async (acceptedFiles) => {
      // A row that failed, was refused as already uploaded, or was skipped does not block its file name: the
      // author can add the fixed file again, and it replaces that row (K21.2 pt 23).
      const replaceableStages = ['failed', 'alreadyExists', 'skipped'];
      const sameAudienceRows = files.filter((entry) => String(entry.audienceType) === String(audienceType));
      const knownFileNames = new Set(
        sameAudienceRows
          .filter((entry) => !replaceableStages.includes(entry.stage))
          .map((entry) => entry.file.name.toLowerCase())
      );
      const duplicateFileNames = [];
      const newFiles = acceptedFiles.filter((file) => {
        const fileName = file.name.toLowerCase();
        if (knownFileNames.has(fileName)) {
          duplicateFileNames.push(file.name);
          return false;
        }
        knownFileNames.add(fileName);
        return true;
      });
      const duplicateMessage = () => `${duplicateFileNames.join(', ')} ${duplicateFileNames.length === 1 ? 'is' : 'are'} already in this batch and ${duplicateFileNames.length === 1 ? 'was' : 'were'} not added.`;
      if (!newFiles.length) {
        setFileError(duplicateMessage());
        return;
      }
      if (!duplicateFileNames.length) setFileError('');

      const replacedRows = sameAudienceRows.filter((entry) => replaceableStages.includes(entry.stage)
        && newFiles.some((file) => file.name.toLowerCase() === entry.file.name.toLowerCase()));
      // The fixed file takes its row's place. The failed upload is removed first, so it no longer counts against
      // the account's room when the new file is checked (at the trial cap it would block its own replacement).
      if (replacedRows.length) {
        const removed = await Promise.all(replacedRows.map((entry) => removeFile(entry.id)));
        // A failed upload that could not be removed would still count: stop here, so its message stays and
        // nothing uploads.
        if (removed.includes(false)) return;
      }
      const selectedCount = files.length - replacedRows.length + newFiles.length;
      // The account's room is checked for the files that are not on the server yet: these new files and any row
      // still uploading. Files the server already has count as used there, once (K21.1 pt 13). The batch total
      // (selectedCount) still goes with each upload for the 5-file limit.
      const roomCount = newFiles.length + files.filter((entry) => entry.stage === 'uploading' && !entry.contentId).length;
      // The storage check counts the same files as the room check: the new ones and any row still uploading.
      const selectedBytes = [...newFiles, ...files.filter((entry) => entry.stage === 'uploading' && !entry.contentId).map((entry) => entry.file)]
        .reduce((total, file) => total + (Number(file.size) || 0), 0);
      setAllowanceChecking(true);
      let allowance;
      try {
        const response = await contentService.checkBatchAllowance(roomCount, selectedBytes);
        allowance = response.data;
        setLimits((current) => ({ ...current, ...allowance }));
      } catch (error) {
        const allowanceError = error.response?.data?.message || 'Unable to verify account content room. Files were not uploaded.';
        setFileError([duplicateFileNames.length ? duplicateMessage() : '', allowanceError].filter(Boolean).join(' '));
        setAllowanceChecking(false);
        return;
      }
      setAllowanceChecking(false);
      if (!allowance.allowed) {
        // The trial block names what to do, with the contact link (K21.2 pt 47).
        const allowanceError = allowance.message || `You've selected ${roomCount} file${roomCount === 1 ? '' : 's'}; your account has room for ${allowance.remaining} more (${allowance.usedCount} of ${allowance.maxCount} used). Remove ${allowance.overBy} file${allowance.overBy === 1 ? '' : 's'}, or delete content you no longer need, or contact Beetsol.`;
        setFileError({ text: [duplicateFileNames.length ? duplicateMessage() : '', allowanceError].filter(Boolean).join(' '), contact: true });
        return;
      }

      const addedFiles = newFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        audienceType,
        scorm: /\.zip$/i.test(file.name),
        stage: 'uploading',
        progress: 0,
        stageIndex: 0,
        contentId: null,
        title: '',
        summary: '',
        skillsText: '',
        saved: false,
      }));
      setFiles((currentFiles) => [...currentFiles, ...addedFiles]);
      void processEntries(addedFiles, selectedCount);
      if (duplicateFileNames.length) setFileError(duplicateMessage());
    },
    onDropRejected: (rejections) => {
      const messages = new Set();
      rejections.forEach(({ errors }) => errors.forEach(({ code }) => {
        if (code === 'file-too-large') messages.add('Each file must be 50 MB or smaller.');
        if (code === 'too-many-files') messages.add(`You can add up to ${MAX_FILES} files at a time.`);
        if (code === 'file-invalid-type') messages.add('One or more files are not a supported type. Use PDF, DOCX, text, MP4, WebM, MP3, M4A, WAV or a SCORM .zip. Old Word files (.doc) need to be saved as .docx first.'); // DOCX (K21.2 pt 20.1.b)
      }));
      setFileError([...messages].join(' '));
    },
  });

  useEffect(() => {
    let mounted = true;

    contentService.getLimits()
      .then((response) => {
        if (mounted) setLimits(response.data);
      })
      .catch((error) => {
        console.error('Unable to load account content usage:', error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const readyIdsKey = files.filter((entry) => entry.stage === 'completed' && entry.contentId).map((entry) => entry.contentId).join(',');
  useEffect(() => {
    if (!readyIdsKey) return;
    contentService.getLimits()
      .then((response) => setLimits((current) => ({ ...current, ...response.data })))
      .catch(() => {});
  }, [readyIdsKey]);

  const baseUsedCount = Number(limits?.usedCount);
  const readyBatchCount = files.filter((entry) => entry.stage === 'completed' && entry.contentId).length;
  const usedCount = Number.isFinite(baseUsedCount) ? Math.max(baseUsedCount, readyBatchCount) : readyBatchCount;
  const maxCount = Number(limits?.maxCount);
  const hasUsage = Number.isFinite(usedCount) && Number.isFinite(maxCount);
  const remaining = hasUsage ? Math.max(maxCount - usedCount, 0) : null;
  const isTrialAccount = limits?.accountType === 'FREE_TRIAL';
  const storageUsed = Number(limits?.totalSize || 0);
  const storageLimit = Number(limits?.maxSize || 0);
  const hasStorageLimit = Number.isFinite(storageUsed) && Number.isFinite(storageLimit) && storageLimit > 0;
  const formatStorageValue = (valueInBytes) => {
    if (!Number.isFinite(valueInBytes) || valueInBytes <= 0) return '0 GB';
    const gb = valueInBytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
    const mb = valueInBytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };
  const readyFiles = files.filter((entry) => entry.stage === 'completed');
  const readyCount = readyFiles.length;
  const indexedPassageCount = files.reduce((total, entry) => total + (Number(entry.chunkCount) || 0), 0);

  // Try it (K21.2 pts 31-36, 45, 48): the same search learners use, limited to this batch's files.
  const readyContentIds = readyFiles.map((entry) => entry.contentId).filter(Boolean);
  const readyContentKey = readyContentIds.join(',');
  const batchSettled = files.length > 0
    && files.every((entry) => ['completed', 'failed', 'skipped', 'alreadyExists', 'limitReached'].includes(entry.stage));
  const [tryItSuggestions, setTryItSuggestions] = useState([]);
  const questionsKeyRef = useRef('');

  // Removed 9 Oct 2026 (K21.2 pts 36, 45): template questions built in the browser from file names.
  // One request per settled batch: the server writes the questions and keeps only ones the index can answer.
  useEffect(() => {
    if (!batchSettled || !readyContentKey) return;
    const key = readyContentKey.split(',').sort().join(',');
    if (questionsKeyRef.current === key) return;
    questionsKeyRef.current = key;
    contentService.getBatchQuestions(readyContentKey.split(',').map(Number))
      .then((response) => setTryItSuggestions(response.data?.questions || []))
      .catch(() => setTryItSuggestions([]));
  }, [batchSettled, readyContentKey]);

  // Removed 9 Oct 2026 (K21.2 pts 31, 34): searching the whole library from Try it, and cutting the answer at 220 characters.
  const runTryIt = async (nextQuestion = tryQuestion) => {
    const question = (nextQuestion || '').trim();
    if (!question || !readyContentKey) return;
    // Each ask is numbered; an answer for an earlier ask, or one the × cleared, is dropped (K21.2 pt 20.1).
    const askId = ++tryAskRef.current;
    const current = () => askId === tryAskRef.current;
    setTryAreaHeight(tryAreaRef.current ? tryAreaRef.current.getBoundingClientRect().height : 0);
    setTryItLoading(true);
    setTryItResponse(null);
    try {
      const outcome = await searchContent(question, { params: { contentIds: readyContentKey } });
      if (!current()) return;
      if (outcome.kind === 'validation') {
        setTryItResponse({ kind: 'validation', message: outcome.message });
        return;
      }
      if (outcome.kind === 'signin') {
        window.location.replace('/login');
        return;
      }
      // The search failed (K15 pts 4-5): the shared error state, whose Try again asks this question again.
      // Removed 10 Oct 2026 (K15 pts 4-5): showing the server's error text in place of the error state, with no Try again.
      if (outcome.kind === 'error') {
        setTryItResponse({ kind: 'error', question });
        return;
      }
      if (outcome.kind === 'gap') {
        setTryItResponse({ kind: 'miss' });
        return;
      }
      const data = outcome.result;
      const seek = data?.seek;
      const seconds = Number(seek?.seconds ?? 0);
      const location = seek?.type === 'page'
        ? `p. ${seek.pageNumber ?? seek.page ?? 1}`
        : seek?.type === 'time'
          ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
          : '';
      setTryItResponse({
        kind: 'answer',
        answer: data.answer,
        contentId: data.contentId,
        source: { name: data.contentTitle || 'Uploaded content', loc: location },
      });
    } catch {
      if (current()) setTryItResponse({ kind: 'error', question });
    } finally {
      if (current()) setTryItLoading(false);
    }
  };

  // The author picks one of the suggested questions or asks their own (K21.2 pt 45.1, agreed 23 Oct). Removed 23 Oct
  // 2026 (K21.2 pt 45.1): asking the first suggested question automatically once the batch was ready, whose answer
  // replaced the suggested questions straight away.

  const updateFile = (id, updates) => {
    setFiles((currentFiles) => currentFiles.map((entry) => entry.id === id ? { ...entry, ...updates } : entry));
  };

  // Remove a file before it finishes (K21.2 pt 25): stop watching it, stop its upload if it is still
  // uploading, and remove the stored upload so it is never processed or published.
  const removeFile = (id) => {
    const entry = files.find((item) => item.id === id);
    removedRowsRef.current.add(id);
    pollCancelsRef.current.get(id)?.();
    pollCancelsRef.current.delete(id);
    uploadAbortsRef.current.get(id)?.();
    uploadAbortsRef.current.delete(id);
    let removal = Promise.resolve(true);
    if (entry?.contentId && entry.stage !== 'completed') {
      removedRecordsRef.current.add(entry.contentId);
      removal = contentService.removeBatchContent(entry.contentId).then(() => true).catch((error) => {
        setFileError(error.response?.data?.message || `Couldn't remove ${entry.file.name} from your library. Delete it from the content list.`);
        return false;
      });
    }
    setFiles((currentFiles) => currentFiles.filter((item) => item.id !== id));
    // Resolves to true once the server has removed the record, false if it could not (a replaced failed row is
    // removed before the room check).
    return removal;
  };

  const processEntries = async (entries, batchFileCount) => {
    if (!audienceType || !entries.length) return;
    activeJobsRef.current += entries.length;
    setUploading(true);
    setFileError('');
    await Promise.all(entries.map(async (entry) => {
      try {
        await uploadAndProcessFile(
          entry.file,
          audienceType,
          batchIdRef.current,
                    batchFileCount,
          (event) => {
            if (removedRowsRef.current.has(entry.id)) {
              if (event.contentId && !removedRecordsRef.current.has(event.contentId)) {
                removedRecordsRef.current.add(event.contentId);
                contentService.removeBatchContent(event.contentId).catch((error) => {
                  setFileError(error.response?.data?.message || `Couldn't remove ${entry.file.name} from your library. Delete it from the content list.`);
                });
              }
              return;
            }
            if (event.stage === 'failed') {
              updateFile(entry.id, {
                stage: 'failed',
                error: event.message,
                attempt: event.attempt,
                maxAttempts: event.maxAttempts,
                progress: 100,
                contentId: event.contentId || null,
                ...(event.failedStage in STAGE_INDEX ? { stageIndex: STAGE_INDEX[event.failedStage] } : {}),
              });
            } else if (event.stage === 'completed') {
              updateFile(entry.id, {
                stage: 'completed',
                progress: 100,
                stageIndex: 4,
                contentId: event.contentId,
                title: event.title || entry.title,
                summary: event.summary || '',
                duration: event.duration ?? '',
                skillsText: (event.skills || []).join(', '),
                scorm: !!event.scorm,
                saved: true,
                chunkCount: Number(event.chunkCount || 0),
                seek: event.seek || null,
                skillsFailed: !!event.skillsFailed,
                error: '',
              });
            } else if (event.stage === 'limitReached') {
              updateFile(entry.id, {
                stage: 'limitReached',
                contentId: event.contentId || entry.contentId,
                error: event.message || 'Workspace daily processing limit reached. This batch can be retried tomorrow.',
                progress: 0,
                stageIndex: 0,
              });
            } else if (event.stage === 'duplicate') {
              updateFile(entry.id, {
                stage: 'duplicate',
                duplicate: event.duplicate,
                contentId: event.contentId || entry.contentId,
                error: '',
              });
            } else if (event.stage === 'skipped') {
              updateFile(entry.id, { stage: 'skipped', contentId: null, error: '' });
            } else {
              const stage = stageProgress[event.stage];
              if (stage) updateFile(entry.id, {
                stage: event.stage,
                progress: stage.progress,
                stageIndex: stage.index,
                attempt: event.attempt,
                maxAttempts: event.maxAttempts,
                error: event.stage === 'retrying' ? event.message : undefined,
                contentId: event.contentId || undefined,
              });
            }
          },
          // The upload's own % shows next to the file's size while it uploads (K21.2 pt 21.1).
          (progress) => updateFile(entry.id, { stage: 'uploading', progress: Math.min(progress * 0.2, 20), stageIndex: 0, uploadPercent: progress }),
          (cancelPolling) => {
            pollCancelsRef.current.set(entry.id, cancelPolling);
            // Removed while it was uploading: remove the stored upload too.
            if (removedRowsRef.current.has(entry.id)) cancelPolling();
          },
          fromFailedSearch ? { prioritized: '1', searchTerm } : {},
          (abortUpload) => uploadAbortsRef.current.set(entry.id, abortUpload)
        );
      } catch (error) {
        // Keep the stage the file reached, so the bar shows where it stopped.
        updateFile(entry.id, {
          stage: error.alreadyExists ? 'alreadyExists' : 'failed',
          progress: error.alreadyExists ? 0 : 100,
          ...(error.alreadyExists ? { stageIndex: 0 } : {}),
          error: error.message
        });
      } finally {
        pollCancelsRef.current.delete(entry.id);
        uploadAbortsRef.current.delete(entry.id);
        activeJobsRef.current -= 1;
        setUploading(activeJobsRef.current > 0);
      }
    }));
  };

  const resolveDuplicate = async (entry, choice) => {
    updateFile(entry.id, { resolvingDuplicate: true, error: '' });
    if (choice === 'skip') pollCancelsRef.current.get(entry.id)?.();
    try {
      await contentService.resolveBatchDuplicate(entry.contentId, choice, entry.duplicate?.id);
      if (choice === 'skip') {
        setFiles((currentFiles) => currentFiles.filter((item) => item.id !== entry.id));
      } else {
        updateFile(entry.id, { stage: 'reading', duplicate: null, resolvingDuplicate: false });
      }
    } catch (error) {
      updateFile(entry.id, {
        resolvingDuplicate: false,
        error: error.response?.data?.message || 'Unable to apply duplicate choice.'
      });
    }
  };

  // The course pop-up's failures (K21.2 pt 46.1 (c)): a refusal the server explains (a 4xx with its message, such as a
  // course with the same name and description) shows that message; anything else reads 'Something went wrong. Try
  // again.', as the screen's other routes answer, until error tracking gives every failure its standard message (S-19
  // (f.6), K3 pt 19).
  const popupFailure = (error) => {
    const status = error?.response?.status;
    return (status >= 400 && status < 500 && error.response.data?.message) || 'Something went wrong. Try again.';
  };

  // forPopup: the save before the course pop-up opens, whose failure reads as the pop-up's do (K21.2 pt 46.1 (c)).
  const saveGeneratedDetails = async (entry, { forPopup = false } = {}) => {
    if (!entry.contentId) return;
    setSavingId(entry.id);
    setFileError('');
    try {
      const response = await contentService.saveBatchMetadata(entry.contentId, {
        title: entry.title,
        summary: entry.summary,
        duration: entry.duration,
        skills: entry.skillsText.split(',').map((skill) => skill.trim()).filter(Boolean),
      });
      // A typed skill that is not in the workspace's list is named on the row, not dropped without a word (K21.2 pt 22).
      updateFile(entry.id, { saved: true, skillsText: (response.data.skills || []).join(', '), skillsNotAdded: response.data.notInList || [] });
      return true;
    } catch (error) {
      setFileError(forPopup ? popupFailure(error) : (error.response?.data?.message || 'Unable to save generated content details.'));
      return false;
    } finally {
      setSavingId(null);
    }
  };

  // Removed 9 Oct 2026 (K21.2 pts 23, 24): Try again uploading the file a second time (it got 409 "already exists").
  // Try again re-runs the file already on the server (no re-upload, no duplicate warning).
  // Only a file whose upload never reached the server is uploaded again.
  const rerunFailedOnServer = async (entry) => {
    updateFile(entry.id, { stage: 'uploaded', stageIndex: 1, progress: 28, error: '', attempt: 0 });
    activeJobsRef.current += 1;
    setUploading(true);
    try {
      await contentService.retryBatchContent(entry.contentId);
      await pollIngestStatus(entry.contentId, (event) => {
        if (event.stage === 'failed') return;
        if (event.stage === 'completed') {
          updateFile(entry.id, {
            stage: 'completed', progress: 100, stageIndex: 4, contentId: event.contentId,
            title: event.title || entry.title, summary: event.summary || '', duration: event.duration ?? '',
            skillsText: (event.skills || []).join(', '), scorm: !!event.scorm, saved: true,
            chunkCount: Number(event.chunkCount || 0), skillsFailed: !!event.skillsFailed, error: '',
          });
          return;
        }
        const stage = stageProgress[event.stage];
        if (stage) updateFile(entry.id, { stage: event.stage, progress: stage.progress, stageIndex: stage.index, error: event.stage === 'retrying' ? event.message : '' });
      }, (cancelPolling) => pollCancelsRef.current.set(entry.id, cancelPolling));
    } catch (error) {
      updateFile(entry.id, {
        stage: 'failed',
        progress: 100,
        error: error.response?.data?.message || error.message || 'This file failed again.',
      });
    } finally {
      pollCancelsRef.current.delete(entry.id);
      activeJobsRef.current -= 1;
      setUploading(activeJobsRef.current > 0);
    }
  };

  const retryFailedEntry = (entry) => {
    if (!audienceType) return;
    if (entry.contentId) {
      void rerunFailedOnServer(entry);
      return;
    }
    const resetEntry = {
      ...entry,
      stage: 'uploading',
      progress: 0,
      uploadPercent: undefined, // the last attempt's upload % doesn't show (K21.2 pt 21.1)
      stageIndex: 0,
      error: '',
      duplicate: null,
      resolvingDuplicate: false,
      contentId: null,
      attempt: 0,
      maxAttempts: 3,
    };
    setFiles((currentFiles) => currentFiles.map((item) => item.id === entry.id ? resetEntry : item));
    void processEntries([resetEntry], files.length);
  };

  const retryAllFailed = () => {
    const failedEntries = files.filter((entry) => entry.stage === 'failed');
    if (!failedEntries.length || !audienceType) return;
    failedEntries.filter((entry) => entry.contentId).forEach((entry) => { void rerunFailedOnServer(entry); });
    const uploadAgain = failedEntries.filter((entry) => !entry.contentId);
    if (!uploadAgain.length) return;

    const resetEntries = uploadAgain.map((entry) => ({
      ...entry,
      stage: 'uploading',
      progress: 0,
      uploadPercent: undefined, // the last attempt's upload % doesn't show (K21.2 pt 21.1)
      stageIndex: 0,
      error: '',
      duplicate: null,
      resolvingDuplicate: false,
      contentId: null,
      attempt: 0,
      maxAttempts: 3,
    }));

    setFiles((currentFiles) => currentFiles.map((entry) => {
      const retryMatch = resetEntries.find((next) => next.id === entry.id);
      return retryMatch ? { ...entry, ...retryMatch } : entry;
    }));

    void processEntries(resetEntries, files.length);
  };

  // "Create a course from this" opens the course pop-up (K21.2 pt 46), from the mock approved on 23 Oct: the name and
  // description written by one AI call from the ready files, and what carries over as pills (the audience, the ready
  // modules, the files' skills as one pill with their names on hover or tap). Unsaved details are saved first, as
  // before, so the course gets the files' titles and skills as the author left them. Removed 23 Oct 2026 (K21.2 pt 46):
  // the button opening the course builder straight away with only the files and the audience filled in, whose files
  // were passed without their type ("12" for "12|content"), so the builder's Save could not add them.
  const createCourseFromBatch = async () => {
    const readyFiles = files.filter((entry) => entry.stage === 'completed' && entry.contentId);
    const readyIds = readyFiles.map((entry) => entry.contentId);
    const kept = courseTextRef.current;
    setCreatingCourse(true);
    // A new try starts without the last one's failure, as a row's save does (K21.2 pt 46.1 (c)).
    setFileError('');
    try {
      // Removed 2 Nov 2026 (K21.2 pt 46.1 (c)): a failed save here showing the row save's own words.
      const savesSucceeded = await Promise.all(readyFiles.filter((entry) => !entry.saved)
        .map((entry) => saveGeneratedDetails(entry, { forPopup: true })));
      if (savesSucceeded.some((success) => !success)) return;
      // Removed 2 Nov 2026 (K21.2 pt 46.1 (a)): a new AI-written name and description each time the pop-up opened.
      const response = await contentService.getBatchCourseDraft(readyIds, { withText: !kept });
      const draft = response.data || {};
      const name = kept ? kept.name : draft.name || '';
      const description = kept ? kept.description : draft.description || '';
      courseTextRef.current = { name, description };
      setCourseDraft({
        name,
        description,
        skills: Array.isArray(draft.skills) ? draft.skills : [],
        contentIds: Array.isArray(draft.contentIds) ? draft.contentIds : [],
        // The audience the files share, as the server works it out (K21.2 pt 46, PR #8 review). Removed 23 Oct 2026 (K21.2
        // pt 46, PR #8 review): the screen's audience switch, which can be changed or cleared after the files are added.
        audienceType: String(draft.audienceType || '3'),
        errors: {},
        error: '',
        creating: false,
      });
    } catch (error) {
      // Removed 2 Nov 2026 (K21.2 pt 46.1 (c)): 'Unable to save the batch details before opening the course builder.'
      setFileError(popupFailure(error));
    } finally {
      setCreatingCourse(false);
    }
  };

  // Closing the pop-up keeps its name and description as left, for the next time it opens (K21.2 pt 46.1 (a)).
  const closeCourseDraft = () => {
    if (!courseDraft || courseDraft.creating) return;
    courseTextRef.current = { name: courseDraft.name, description: courseDraft.description };
    setCourseDraft(null);
  };

  // Create course makes the course as an Ordinary draft, with the batch's ready files in order, its audience and the
  // files' skills, and opens it in the course builder, where modules are ordered, added or removed (K21.2 pt 46; the
  // row's note: "created as an Ordinary draft"). The name and description are checked as the builder checks them, with
  // its messages.
  const createCourseFromDraft = async () => {
    if (!courseDraft || courseDraft.creating) return;
    const errors = {};
    if (!courseDraft.name.trim()) errors.name = 'Course name is required';
    if (!courseDraft.description.trim()) errors.description = 'Course description is required';
    if (Object.keys(errors).length) {
      setCourseDraft((draft) => ({ ...draft, errors }));
      return;
    }
    setCourseDraft((draft) => ({ ...draft, errors: {}, error: '', creating: true }));
    try {
      const response = await courseService.createCourse({
        course_name: courseDraft.name.trim(),
        // Removed 23 Oct 2026 (K21.2 pt 46, PR #8 review): the description sent as typed, which the course pages then
        // showed as HTML.
        description: asEditorHtml(courseDraft.description),
        course_status: '1',
        content_ordered: '0',
        audience_type: String(courseDraft.audienceType || '3'),
        selected_contents: courseDraft.contentIds.map((contentId) => ({ cust_id: `${contentId}|content` })),
        skills: JSON.stringify(courseDraft.skills.map((skill) => skill.id)),
      });
      navigate(`/course/form/${response.data.courseId}`);
    } catch (error) {
      // Removed 2 Nov 2026 (K21.2 pt 46.1 (c)): 'Something went wrong. Please try again.', and the server's own words for
      // a server failure ('Error creating course').
      setCourseDraft((draft) => draft && ({ ...draft, creating: false, error: popupFailure(error) }));
    }
  };

  // The screen in the app's own look, from the mock Roshan approved on 23 Oct (K21.2 pt 20.1): DM Sans throughout (no
  // code-style type), white cards on the app's canvas, the files in one list, and Try it as a light card. Removed 23 Oct
  // 2026 (K21.2 pt 20.1): the prototype's dark Try it panel and monospace labels, counts and chips, and the files as
  // separate cards.
  const card = { backgroundColor: T.color.bgPrimary, border: `1px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, p: '18px 20px' };
  const eyebrow = { fontFamily: T.font.family, fontSize: T.font.size.xs, letterSpacing: T.font.letterSpacing.label, textTransform: 'uppercase', color: T.color.textTertiary };
  const stageChip = (entry) => {
    if (entry.stage === 'completed') return { color: T.color.status.activeText, backgroundColor: T.color.status.activeBg };
    if (entry.stage === 'failed') return { color: T.color.status.alertText, backgroundColor: T.color.status.alertBg };
    if (['duplicate', 'alreadyExists', 'skipped', 'limitReached'].includes(entry.stage)) return { color: T.color.textSecondary, backgroundColor: T.color.bgTertiary };
    return { color: T.color.status.warnText, backgroundColor: T.color.status.warnBg };
  };
  const tryItShowsResult = tryItLoading || !!tryItResponse;
  // The × also drops an answer still on its way (PR #8 review), so nothing lands under an empty box.
  const clearTryIt = () => {
    tryAskRef.current += 1;
    setTryItLoading(false);
    setTryQuestion('');
    setTryItResponse(null);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3, md: 0 }, py: { xs: 2, sm: 3, md: '34px' }, pb: { md: '90px' }, color: T.color.textPrimary, fontFamily: T.font.family, display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ ...eyebrow, mb: 0.5 }}>Content library</Typography>
          <Typography component="h1" sx={{ fontFamily: T.font.family, fontSize: { xs: T.font.size.xxl, sm: T.font.size.display }, fontWeight: T.font.weight.medium, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Add content
          </Typography>
        </Box>
        {/* The screen's '…' (K21.2 pt 46.1 (d)). Removed 2 Nov 2026 (K21.2 pt 46.1 (d)): 'Preparing course...' */}
        {readyCount >= 2 && (
          <Button variant="outlined" onClick={createCourseFromBatch} disabled={creatingCourse} sx={{ ...btnOut, whiteSpace: 'nowrap' }}>
            {creatingCourse ? 'Preparing course…' : '+ Create a course from this'}
          </Button>
        )}
      </Box>
      <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.lg, color: T.color.textSecondary, lineHeight: 1.6, mt: '-8px', maxWidth: '62ch' }}>
        Drop your files in. Beetsol reads each one, writes the title, length, summary and skills, and makes it answerable.
      </Typography>
      {fromFailedSearch && (
        // The notice's colours are the theme's information pair (PR #8 review). Removed 23 Oct 2026 (K21.2 pt 20.1): the
        // mock's dark blue written here as a hex.
        <Box role="status" sx={{ backgroundColor: T.color.status.infoBg, color: T.color.status.infoText, borderRadius: T.radius.md, p: '12px 16px', fontFamily: T.font.family, fontSize: T.font.size.md }}>
          Adding content for the search "{searchTerm}". Files you add here are marked as a priority in search
          {' '}results. A single file takes the search term as its title, which you can edit.
        </Box>
      )}

      <Box component="section" aria-labelledby="add-content-audience" sx={card}>
        <Typography id="add-content-audience" component="div" sx={{ fontFamily: T.font.family, fontSize: T.font.size.lg, fontWeight: T.font.weight.medium }}>
          Who can see this batch<span style={{ color: T.color.alertDot, marginLeft: 2 }}>*</span>
        </Typography>
        <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.textSecondary, mt: 0.25 }}>
          Applies to every file you add now. You can change it per file afterwards.
        </Typography>

        <ToggleButtonGroup
          exclusive
          value={audienceType}
          onChange={(event, value) => setAudienceType(value)}
          aria-label="Who can see this batch"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.75, '& .MuiToggleButtonGroup-grouped': { border: '1px solid', borderColor: T.color.borderTertiary, borderRadius: `${T.radius.pill} !important`, mx: '0 !important' } }}
        >
          {audienceOptions.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              aria-label={option.label}
              sx={{
                px: 2,
                py: '9px',
                color: T.color.textSecondary,
                fontFamily: T.font.family,
                fontSize: T.font.size.md,
                textTransform: 'none',
                backgroundColor: T.color.bgPrimary,
                '&.Mui-selected': { color: T.color.bgPrimary, backgroundColor: T.color.textPrimary, borderColor: T.color.textPrimary },
                '&.Mui-selected:hover': { backgroundColor: T.color.textPrimary },
                '&:focus-visible': { outline: `2px solid ${T.color.brand}`, outlineOffset: 2 },
              }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ mt: 2, pt: 1.75, borderTop: `1px solid ${T.color.borderTertiary}`, display: 'flex', flexDirection: 'column', gap: 0.75, color: T.color.textSecondary }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography component="span" sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: isTrialAccount && remaining === 0 ? T.color.alertDot : T.color.textSecondary }}>
              Account content: <Box component="b" sx={{ fontWeight: T.font.weight.medium, color: isTrialAccount && remaining === 0 ? T.color.alertDot : T.color.textPrimary }}>{hasUsage ? usedCount : '—'}</Box> of <Box component="b" sx={{ fontWeight: T.font.weight.medium, color: isTrialAccount && remaining === 0 ? T.color.alertDot : T.color.textPrimary }}>{hasUsage ? maxCount : '—'}</Box> used
            </Typography>
            <Box sx={{ width: 120, height: 5, overflow: 'hidden', borderRadius: 99, backgroundColor: T.color.bgCream }}>
              <Box sx={{ width: hasUsage ? `${Math.min((usedCount / Math.max(maxCount, 1)) * 100, 100)}%` : 0, height: '100%', backgroundColor: isTrialAccount && remaining === 0 ? T.color.alertDot : T.color.textPrimary, transition: 'width 0.4s ease' }} />
            </Box>
            {/* Production is never blocked, so its line ends at the meter; a free trial keeps 'room for K more', and
                'limit reached' at its cap (K21.2 pt 47.1, agreed 22 Oct). Removed 23 Oct 2026 (K21.2 pt 47.1): Production's
                '· room for K more' and '· N over your set limit (tracked, not blocked)'. */}
            {hasUsage && isTrialAccount && <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: remaining === 0 ? T.color.alertDot : T.color.textSecondary }}>
              · {remaining === 0 ? 'limit reached' : `room for ${remaining} more`}
            </Typography>}
            {/* The app's contact address is support@ (K21.2 pt 47.2). Removed 2 Nov 2026 (K21.2 pt 47.2): info@beetsol.com. */}
            {hasUsage && isTrialAccount && remaining === 0 && (
              <Typography component="a" href="mailto:support@beetsol.com" sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.alertDot, textDecoration: 'underline' }}>
                Contact Beetsol
              </Typography>
            )}
          </Box>
          {hasStorageLimit && (
            <Typography component="span" sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.textSecondary }}>
              Storage: <Box component="b" sx={{ fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>{formatStorageValue(storageUsed)}</Box> of <Box component="b" sx={{ fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>{formatStorageValue(storageLimit)}</Box>
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        {...getRootProps()}
        component="section"
        aria-label="Add files"
        sx={{
          minHeight: 200,
          p: '28px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 0.75,
          border: '1.5px dashed',
          borderColor: isDragActive ? T.color.brand : T.color.borderSecondary,
          borderRadius: T.radius.lg,
          backgroundColor: isDragActive ? T.color.bgCream : T.color.bgPrimary,
          opacity: !audienceType ? 0.45 : 1,
          pointerEvents: !audienceType ? 'none' : 'auto',
          transition: `all ${T.transition}`,
        }}
      >
        <input {...getInputProps()} />
        <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.lg, fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>
          {isDragActive ? 'Drop files to add them' : 'Drop files here'}
        </Typography>
        <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.textSecondary, lineHeight: 1.6, mb: 1 }}>
          {/* Add content's wording (K21.2 pt 20.1.b): DOCX, 'Checking storage…' and 'N files, max 5' below */}
          PDF, DOCX, text, video, audio, SCORM · up to 50 MB each · up to 5 files at a time
        </Typography>
        <Button variant="contained" onClick={open} disabled={!audienceType || files.length >= MAX_FILES || allowanceChecking} sx={btnSolid}>
          {allowanceChecking ? 'Checking storage…' : 'Choose files'}
        </Button>
        {!audienceType && (
          <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.xs, color: T.color.textTertiary }}>
            Choose who can see this batch before adding files.
          </Typography>
        )}
      </Box>

      {!!fileError && (
        <Alert severity="error" sx={{ mt: '-8px', fontFamily: T.font.family }}>
          {typeof fileError === 'string' || !fileError.contact || !fileError.text.endsWith('contact Beetsol.')
            ? (typeof fileError === 'string' ? fileError : fileError.text)
            : (
              <>
                {fileError.text.slice(0, -'contact Beetsol.'.length)}
                {/* support@ (K21.2 pt 47.2). Removed 2 Nov 2026 (K21.2 pt 47.2): info@beetsol.com. */}
                <Box component="a" href="mailto:support@beetsol.com" sx={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>contact Beetsol</Box>.
              </>
            )}
        </Alert>
      )}

      {files.length > 0 && (
        <>
          <Box component="section" aria-label="Batch progress" sx={{ ...card, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 16px' }}>
            <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, fontWeight: T.font.weight.medium, whiteSpace: 'nowrap', color: T.color.textPrimary }}>
              {readyCount} of {files.length} ready
            </Typography>
            <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.sm, whiteSpace: 'nowrap', color: T.color.textTertiary }}>
              {indexedPassageCount} passages indexed
            </Typography>
            <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.sm, whiteSpace: 'nowrap', color: T.color.textTertiary }}>
              {files.length} {files.length === 1 ? 'file' : 'files'}, max {MAX_FILES}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={files.length ? (readyCount / files.length) * 100 : 0}
              sx={{ flex: '1 1 140px', height: 5, borderRadius: 99, backgroundColor: T.color.bgCream, '& .MuiLinearProgress-bar': { backgroundColor: T.color.positive, borderRadius: 99, transition: 'transform 0.5s ease' } }}
            />
            {files.some((entry) => entry.stage === 'failed') && (
              <Button variant="outlined" onClick={retryAllFailed} disabled={uploading} sx={{ ...btnOut, whiteSpace: 'nowrap' }}>
                Retry all failed
              </Button>
            )}
          </Box>
          {/* The author can leave while files process (K21.2 pts 20, 28). */}
          <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.sm, color: T.color.textSecondary, mt: '-12px' }}>
            Leave this page whenever you like. Anything still indexing keeps its status in the content list.
          </Typography>

          <Box component="section" aria-label="Files in this batch" sx={{ display: 'flex', flexDirection: 'column', border: `1px solid ${T.color.borderTertiary}`, borderRadius: T.radius.lg, backgroundColor: T.color.bgPrimary, overflow: 'hidden' }}>
          {files.map((entry, fileIndex) => {
            const currentStage = stageProgress[entry.stage];
            const statusLabel = entry.stage === 'uploading'
              ? 'Uploading'
              : entry.stage === 'failed'
                ? 'Failed'
                : entry.stage === 'duplicate'
                  ? 'Duplicate'
                  : entry.stage === 'alreadyExists'
                    ? 'Already uploaded'
                  : entry.stage === 'skipped'
                    ? 'Skipped'
                : entry.stage === 'limitReached'
                  ? 'Daily limit reached'
                : entry.stage === 'retrying'
                  ? `Retrying ${entry.attempt || 1}/${entry.maxAttempts || 3}`
                  : entry.scorm && entry.stage === 'completed'
                    ? 'Playable'
                  : currentStage?.label || 'Waiting';
            const stageIndex = entry.stageIndex ?? currentStage?.index ?? 0;
            // A ready row opens to its details; its arrow folds them away and back (the mock's ready rows).
            const detailsOpen = entry.stage === 'completed' && !collapsedRows.has(entry.id);

            return (
              <Box key={entry.id} sx={{ borderTop: fileIndex ? `1px solid ${T.color.borderTertiary}` : 0, animation: 'ingestIn 0.32s ease both', animationDelay: `${fileIndex * 80}ms`, '@keyframes ingestIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '14px 18px', flexWrap: 'wrap' }}>
                  <Typography sx={{ flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere', fontFamily: T.font.family, fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>
                    {entry.file.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.sm, color: T.color.textTertiary, whiteSpace: 'nowrap' }}>
                    {formatFileSize(entry.file.size)}
                    {/* While it uploads, the upload's % next to the size (K21.2 pt 21.1) */}
                    {entry.stage === 'uploading' && Number.isFinite(entry.uploadPercent) ? ` · ${entry.uploadPercent}%` : ''}
                  </Typography>
                  <Box component="span" sx={{ fontFamily: T.font.family, fontSize: T.font.size.xs, fontWeight: T.font.weight.medium, borderRadius: T.radius.pill, px: '9px', py: '3px', whiteSpace: 'nowrap', ...stageChip(entry) }}>
                    {statusLabel}
                  </Box>
                  {entry.stage === 'completed' ? (
                    <IconButton
                      size="small"
                      aria-label={detailsOpen ? `Fold away ${entry.file.name}` : `Open ${entry.file.name}`}
                      aria-expanded={detailsOpen}
                      onClick={() => toggleRow(entry.id)}
                      sx={{ color: T.color.textTertiary }}
                    >
                      {detailsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  ) : entry.stage !== 'skipped' && (
                    <IconButton aria-label={`Remove ${entry.file.name}`} size="small" onClick={() => removeFile(entry.id)} sx={{ color: T.color.textTertiary }}>
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                  </Box>
                </Box>

                {entry.scorm ? (
                  <Typography sx={{ px: '18px', pb: 2, fontFamily: T.font.family, fontSize: T.font.size.sm, color: T.color.textSecondary }}>
                    {entry.stage === 'completed' ? 'Playable SCORM package' : 'SCORM package'}
                  </Typography>
                ) : entry.stage !== 'completed' && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75, px: '18px', pb: 2 }} aria-label={`Processing stage: ${statusLabel}`}>
                    {PIPELINE_STAGES.map((stage, stageNumber) => {
                      const done = stageNumber < stageIndex;
                      const now = stageNumber === stageIndex && !['failed', 'alreadyExists'].includes(entry.stage);
                      const failedHere = entry.stage === 'failed' && stageNumber === stageIndex;
                      return (
                        <Box key={stage} sx={{ minWidth: 0 }}>
                          <Box sx={{ height: 4, borderRadius: 99, mb: '5px', backgroundColor: failedHere ? T.color.alertDot : done ? T.color.positive : now ? T.color.warning : T.color.bgTertiary }} />
                          <Typography sx={{ fontFamily: T.font.family, fontSize: { xs: '9px', sm: T.font.size.xs }, color: now ? T.color.textPrimary : failedHere ? T.color.status.alertText : T.color.textTertiary, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                            {stage}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {entry.error && (
                  <Box sx={{ px: '18px', pb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
                    <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: entry.stage === 'alreadyExists' ? T.color.textSecondary : T.color.status.alertText }}>
                      {entry.error || 'This file did not finish processing. Something went wrong while it was being read or indexed.'}
                    </Typography>
                    {entry.stage === 'failed' && (
                      <Button size="small" variant="outlined" onClick={() => retryFailedEntry(entry)} disabled={uploading} sx={{ ...btnOut, ...btnSmall }}>
                        Try again
                      </Button>
                    )}
                  </Box>
                )}

                {entry.stage === 'duplicate' && (
                  <Box sx={{ borderTop: `1px solid ${T.color.borderTertiary}`, backgroundColor: T.color.bgCream, p: '12px 18px', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ flex: '1 1 220px', minWidth: 0, fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.textPrimary }}>
                      Looks like <Box component="strong">{entry.duplicate?.name || 'matching content'}</Box>, already in your library.
                    </Typography>
                    <Button size="small" variant="outlined" disabled={entry.resolvingDuplicate} onClick={() => resolveDuplicate(entry, 'replace')} sx={{ ...btnOut, ...btnSmall }}>
                      Replace it
                    </Button>
                    <Button size="small" variant="outlined" disabled={entry.resolvingDuplicate} onClick={() => resolveDuplicate(entry, 'add-anyway')} sx={{ ...btnOut, ...btnSmall }}>
                      Add anyway
                    </Button>
                    <Button size="small" variant="text" disabled={entry.resolvingDuplicate} onClick={() => resolveDuplicate(entry, 'skip')} sx={{ ...btnGhost, ...btnSmall }}>
                      Skip
                    </Button>
                  </Box>
                )}

                {detailsOpen && (
                  <Box sx={{ p: '0 18px 18px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 120px' }, gap: '12px' }}>
                      <Box>
                        <MetadataLabel htmlFor={`${entry.id}-title`}>Title</MetadataLabel>
                        <TextField id={`${entry.id}-title`} size="small" fullWidth value={entry.title} onChange={(event) => updateFile(entry.id, { title: event.target.value, saved: false })} inputProps={{ 'aria-label': 'Title' }} sx={metadataFieldSx} />
                      </Box>
                      <Box>
                        <MetadataLabel htmlFor={`${entry.id}-duration`}>Length (minutes)</MetadataLabel>
                        <TextField id={`${entry.id}-duration`} size="small" fullWidth type="number" inputProps={{ min: 0, 'aria-label': 'Length in minutes' }} value={entry.duration || ''} onChange={(event) => updateFile(entry.id, { duration: event.target.value, saved: false })} sx={metadataFieldSx} />
                      </Box>
                      <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                        <MetadataLabel htmlFor={`${entry.id}-summary`}>Summary</MetadataLabel>
                        <TextField id={`${entry.id}-summary`} size="small" fullWidth multiline minRows={2} value={entry.summary} onChange={(event) => updateFile(entry.id, { summary: event.target.value, saved: false })} inputProps={{ 'aria-label': 'Summary' }} sx={metadataFieldSx} />
                      </Box>
                      <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                        <MetadataLabel htmlFor={`${entry.id}-skill-draft`}>Skills</MetadataLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minHeight: 36 }}>
                          {entry.skillsText.split(',').map((skill) => skill.trim()).filter(Boolean).map((skill) => (
                            <Chip
                              key={skill}
                              label={skill}
                              size="small"
                              onDelete={() => updateFile(entry.id, { skillsText: entry.skillsText.split(',').map((item) => item.trim()).filter((item) => item && item !== skill).join(', '), saved: false })}
                              sx={{
                                height: 28,
                                borderRadius: T.radius.pill,
                                backgroundColor: T.color.bgCream,
                                color: T.color.textPrimary,
                                fontFamily: T.font.family,
                                fontSize: T.font.size.sm,
                                '& .MuiChip-label': { px: 1.25 },
                                '& .MuiChip-deleteIcon': { color: T.color.textTertiary, fontSize: 16, '&:hover': { color: T.color.alertDot } },
                              }}
                            />
                          ))}
                          <TextField
                            id={`${entry.id}-skill-draft`}
                            size="small"
                            placeholder="+ Add skill"
                            value={entry.skillDraft || ''}
                            onChange={(event) => updateFile(entry.id, { skillDraft: event.target.value })}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' && entry.skillDraft?.trim()) {
                                event.preventDefault();
                                const skills = entry.skillsText.split(',').map((item) => item.trim()).filter(Boolean);
                                if (!skills.includes(entry.skillDraft.trim())) skills.push(entry.skillDraft.trim());
                                updateFile(entry.id, { skillsText: skills.join(', '), skillDraft: '', saved: false });
                              }
                            }}
                            inputProps={{ 'aria-label': 'Add a skill' }}
                            sx={{
                              width: 145,
                              ...metadataFieldSx,
                              '& .MuiOutlinedInput-root': {
                                ...metadataFieldSx['& .MuiOutlinedInput-root'],
                                borderRadius: T.radius.pill,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: T.color.borderSecondary, borderStyle: 'dashed' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.color.textSecondary },
                              },
                              '& .MuiInputBase-input': { ...metadataFieldSx['& .MuiInputBase-input'], py: '5px', px: 1.25, fontSize: T.font.size.sm },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    {entry.skillsNotAdded?.length > 0 && (
                      <Typography role="status" sx={{ fontFamily: T.font.family, fontSize: T.font.size.xs, color: T.color.status.alertText, mt: 1.5 }}>
                        {`Not added, because ${entry.skillsNotAdded.length === 1 ? 'it is' : 'they are'} not in this workspace's skills list: ${entry.skillsNotAdded.join(', ')}. An admin can add skills under Skills.`}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
                      <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.xs, color: T.color.textSecondary, maxWidth: '52ch' }}>
                        {entry.scorm
                          ? 'Description and skills are optional for SCORM packages.'
                          : entry.skillsFailed
                            ? "Written by Beetsol from the file. Skills couldn't be suggested this time, so add them above. Edit anything that's off."
                            : "Written by Beetsol from the file. Edit anything that's off."}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.saved && <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.sm, color: T.color.positive }}>Saved to content</Typography>}
                        {/* The screen's '…' (K21.2 pt 46.1 (d)). Removed 2 Nov 2026 (K21.2 pt 46.1 (d)): 'Saving...' */}
                        <Button variant="contained" disabled={savingId === entry.id || entry.saved} onClick={() => saveGeneratedDetails(entry)} sx={btnSolid}>
                          {savingId === entry.id ? 'Saving…' : entry.saved ? 'Saved' : 'Save details'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
          </Box>
        </>
      )}

      {/* Try it (K21.2 pts 20.1, 31-36, 45.1, 48): the question box first, with its hint as the placeholder; under it, the
          questions the batch can now answer. Choosing one, or asking anything else, replaces them with the answer and its
          source, or with the gap message; the × in the box clears the search and brings the questions back.
          Removed 23 Oct 2026 (K21.2 pt 20.1): the hint as a line above the box ('Answers come only from the files in this
          batch, the same way learners will see them.', or 'Available as soon as the first file is ready.'), the example
          placeholder "How do I reset a customer's SSO connection?", and the suggested questions staying on screen above
          the result, while a search ran and after any result: an answer, the gap message, a validation message or an
          error. */}
      <Box component="section" aria-label="Try it" sx={card}>
        <Typography sx={{ ...eyebrow, mb: 0.25 }}>Try it</Typography>
        <Typography component="h2" sx={{ fontFamily: T.font.family, fontSize: T.font.size.lg, fontWeight: T.font.weight.medium }}>
          Ask your content a question
        </Typography>

        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!tryItLoading && readyCount > 0) runTryIt(tryQuestion);
          }}
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.75 }}
        >
          <Box sx={{ flex: '1 1 240px', minWidth: 0, position: 'relative', display: 'flex' }}>
            <TextField
              fullWidth
              disabled={readyCount === 0}
              value={tryQuestion}
              onChange={(event) => setTryQuestion(event.target.value)}
              placeholder={readyCount > 0 ? 'Answers come only from the files in this batch' : 'Available as soon as the first file is ready'}
              inputProps={{ 'aria-label': 'Ask your content a question' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: T.color.bgPrimary,
                  borderRadius: T.radius.md,
                  '& fieldset': { borderColor: T.color.borderSecondary },
                  '&:hover fieldset': { borderColor: T.color.borderPrimary },
                  '&.Mui-focused fieldset': { borderColor: T.color.brand, borderWidth: 1 },
                },
                '& .MuiInputBase-input': {
                  fontFamily: T.font.family,
                  fontSize: T.font.size.lg,
                  color: T.color.textPrimary,
                  padding: '11px 44px 11px 14px',
                  '&::placeholder': { color: T.color.textTertiary, opacity: 1 },
                },
              }}
            />
            {(tryQuestion || tryItResponse) && (
              <IconButton
                aria-label="Clear the question"
                onClick={clearTryIt}
                sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, backgroundColor: T.color.bgTertiary, color: T.color.textSecondary, '&:hover': { backgroundColor: T.color.borderTertiary, color: T.color.textPrimary } }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
          <Button type="submit" variant="contained" disabled={tryItLoading || readyCount === 0 || !tryQuestion.trim()} sx={{ ...btnSolid, minWidth: 80 }}>
            {tryItLoading ? 'Checking…' : 'Ask'}
          </Button>
        </Box>

        {/* While it checks, the area keeps the height it had (the questions, or the last answer), so the page doesn't
            move (K21.2 pt 36.1). Removed 2 Nov 2026 (K21.2 pt 36.1): the area emptying while it checked. */}
        <Box ref={tryAreaRef} sx={{ mt: 2, minHeight: tryItLoading ? `${tryAreaHeight}px` : undefined }}>
          {!tryItShowsResult && tryItSuggestions.length > 0 && (
            <Box>
              <Typography sx={{ ...eyebrow, mb: 1 }}>Questions this batch can now answer</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tryItSuggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outlined"
                    onClick={() => {
                      setTryQuestion(suggestion);
                      runTryIt(suggestion);
                    }}
                    sx={{ borderRadius: T.radius.pill, borderColor: T.color.borderSecondary, color: T.color.textPrimary, backgroundColor: T.color.bgSecondary, fontFamily: T.font.family, fontSize: T.font.size.md, fontWeight: T.font.weight.regular, textTransform: 'none', textAlign: 'left', px: 1.5, py: 0.75, '&:hover': { borderColor: T.color.textPrimary, backgroundColor: T.color.bgSecondary } }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {(tryItResponse && tryItResponse.kind === 'answer') && (
            <Box>
              <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.lg, lineHeight: 1.6, color: T.color.textPrimary, mb: 1.5, maxWidth: '68ch', whiteSpace: 'pre-line' }}>
                {tryItResponse.answer}
              </Typography>
              <Button
                variant="outlined"
                disabled={!tryItResponse.contentId}
                onClick={() => {
                  if (tryItResponse.contentId) navigate(`/contents/form/${tryItResponse.contentId}`);
                }}
                sx={{ borderRadius: T.radius.sm, borderColor: T.color.borderSecondary, color: T.color.textPrimary, backgroundColor: T.color.bgPrimary, textTransform: 'none', fontFamily: T.font.family, fontSize: T.font.size.md, fontWeight: T.font.weight.regular, px: 1.25, py: 0.75, maxWidth: '100%', '&:hover': { borderColor: T.color.textPrimary, backgroundColor: T.color.bgPrimary } }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {`Source: ${tryItResponse.source.name}`}
                  <Box component="span" sx={{ color: T.color.textTertiary }}>{`${tryItResponse.source.loc ? ` · ${tryItResponse.source.loc}` : ''} →`}</Box>
                </Box>
              </Button>
            </Box>
          )}

          {(tryItResponse && tryItResponse.kind === 'miss') && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Box component="span" sx={{ ...eyebrow, color: T.color.textSecondary, fontWeight: 600 }}>No answer</Box>
              {/* The gap message as it shows on every search screen: the warning text on its pale box (K2.3 pt 14.1).
                  Removed 2 Nov 2026 (K2.3 pt 14.1): the warning text alone, with no box. */}
              <ContentSearchMessage kind="gap" sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.status.warnText, background: T.color.status.warnBg, border: `0.5px solid ${T.color.status.warnText}`, borderRadius: T.radius.md, padding: '6px 12px' }} />
            </Box>
          )}

          {(tryItResponse && tryItResponse.kind === 'validation') && (
            <ContentSearchMessage kind="validation" message={tryItResponse.message} sx={{ fontFamily: T.font.family, fontSize: T.font.size.md }} />
          )}

          {(tryItResponse && tryItResponse.kind === 'error') && (
            <ContentSearchMessage kind="error" onRetry={() => runTryIt(tryItResponse.question)} />
          )}
        </Box>

        {readyCount > 0 && (
          <Typography sx={{ mt: 1.75, fontFamily: T.font.family, fontSize: T.font.size.sm, color: T.color.textTertiary }}>
            Try something your files don't cover to see what happens.
          </Typography>
        )}
      </Box>

      {/* Back to the Content Library, by the menu's name for it, as an outline button like the screen's others (agreed
          23 Oct). Removed 23 Oct 2026 (K21.2 pt 20.1): 'Back to contents' in grey. */}
      <Box>
        <Button variant="outlined" onClick={() => navigate('/contents')} sx={btnOut}>
          Back to Content Library
        </Button>
      </Box>

      {/* The course pop-up (K21.2 pt 46), from the mock approved on 23 Oct, in the prototype's words. Cancel, Escape or a
          click outside closes it, except while the course is being made. */}
      <Dialog
        open={!!courseDraft}
        onClose={closeCourseDraft}
        aria-labelledby="course-popup-title"
        PaperProps={{ sx: { borderRadius: T.radius.lg, maxWidth: 520, width: '100%', m: 2, p: '22px', display: 'flex', flexDirection: 'column', gap: '14px', boxShadow: '0 24px 60px rgba(17,17,16,.22)', backgroundColor: T.color.bgPrimary } }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(17,17,16,.42)' } } }}
      >
        {courseDraft && (() => {
          const audienceLabel = (audienceOptions.find((option) => option.value === String(courseDraft.audienceType)) || audienceOptions[2]).label;
          const moduleCount = courseDraft.contentIds.length;
          const skillCount = courseDraft.skills.length;
          const pill = { display: 'inline-flex', alignItems: 'center', backgroundColor: T.color.bgTertiary, color: T.color.textPrimary, borderRadius: T.radius.pill, px: '10px', py: '4px', fontFamily: T.font.family, fontSize: T.font.size.sm, lineHeight: 1.4 };
          const setField = (field) => (event) => {
            const value = event.target.value;
            setCourseDraft((draft) => ({ ...draft, [field]: value, errors: { ...draft.errors, [field]: undefined } }));
          };
          return (
            <>
              <Typography id="course-popup-title" component="h2" sx={{ fontFamily: T.font.family, fontSize: T.font.size.xl, fontWeight: T.font.weight.medium, letterSpacing: '-0.01em', color: T.color.textPrimary, m: 0 }}>
                Create a course from this batch
              </Typography>
              <Typography sx={{ fontFamily: T.font.family, fontSize: T.font.size.md, color: T.color.textSecondary, lineHeight: 1.6, mt: '-10px' }}>
                Name it and confirm. You can order and add or remove modules on the next screen.
              </Typography>
              <Box>
                <MetadataLabel htmlFor="course-popup-name">Course name</MetadataLabel>
                <TextField id="course-popup-name" fullWidth size="small" autoFocus value={courseDraft.name} onChange={setField('name')}
                  error={!!courseDraft.errors.name} helperText={courseDraft.errors.name} sx={metadataFieldSx} />
              </Box>
              <Box>
                <MetadataLabel htmlFor="course-popup-description">Description</MetadataLabel>
                <TextField id="course-popup-description" fullWidth multiline minRows={3} value={courseDraft.description} onChange={setField('description')}
                  error={!!courseDraft.errors.description} helperText={courseDraft.errors.description} sx={metadataFieldSx} />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                <Typography component="small" sx={{ width: '100%', fontFamily: T.font.family, fontSize: T.font.size.xs, color: T.color.textSecondary }}>
                  Carried over from this batch
                </Typography>
                <Box component="span" sx={{ ...pill, backgroundColor: T.color.brandLight, color: T.color.brand }}>{audienceLabel}</Box>
                <Box component="span" sx={pill}>{`${moduleCount} ready ${moduleCount === 1 ? 'module' : 'modules'}`}</Box>
                {skillCount > 0 && (
                  // The skills as one pill, their names on hover, focus or tap, never as a list (K21.2 pt 20.1).
                  <Tooltip title={courseDraft.skills.map((skill) => skill.name).join(', ')} describeChild enterTouchDelay={0} leaveTouchDelay={4000}>
                    <Box component="button" type="button" sx={{ ...pill, border: 0, cursor: 'help' }}>
                      {`${skillCount} ${skillCount === 1 ? 'skill' : 'skills'}`}
                    </Box>
                  </Tooltip>
                )}
              </Box>
              {!!courseDraft.error && (
                <Alert severity="error" sx={{ fontFamily: T.font.family }}>{courseDraft.error}</Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {/* A pop-up's Cancel takes the one outlined style (K2.3 pt 15). Removed 2 Nov 2026 (K2.3 pt 15): the
                    borderless grey look (btnGhost). */}
                <Button variant="outlined" onClick={closeCourseDraft} disabled={courseDraft.creating} sx={btnOut}>Cancel</Button>
                <Button variant="contained" onClick={createCourseFromDraft} disabled={courseDraft.creating} sx={btnSolid}>
                  {courseDraft.creating ? 'Opening course builder…' : 'Create course'}
                </Button>
              </Box>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default AddContent;